/**
 * AdminSourceHealth — Source Health panel (the "source-backed" guarantee).
 *
 * Summary of the nightly source-link audit + per-source actions:
 * Recheck now · Open source · Replace source · Add archived copy ·
 * Mark temporarily unavailable · Mark reviewed.
 * Sources are never auto-removed; dossier claims are never auto-altered.
 */
import React, { useCallback, useEffect, useState } from "react";
import {
  Link2, RefreshCw, Loader2, ExternalLink, Replace, Archive,
  Clock, CheckCircle2, AlertTriangle, Radar, EyeOff, Eye, XCircle,
} from "lucide-react";
import { toast } from "sonner";
import {
  adminSourceLinkHealth, adminSourceLinkAuditRun, adminSourceLinkRecheck,
  adminSourceLinkReplace, adminSourceLinkArchiveCopy, adminSourceLinkMark,
  adminSourceLinkDecide,
} from "../../lib/api";
import EvidenceRecoveryQueue from "./EvidenceRecoveryQueue";

const _fmt = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
};

const STATUS_COLOR = {
  PASS: "#3FB950",
  REDIRECTED: "#58A6FF",
  "TEMPORARILY UNAVAILABLE": "#E0A526",
  "ACCESS RESTRICTED": "#E0A526",
  BROKEN: "#D92D20",
  REMOVED: "#D92D20",
  "NEEDS MANUAL REVIEW": "#D9A56C",
  "RECOVERED (ARCHIVED)": "#9FA8D4",
};

const Chip = ({ label, value, color, testid }) => (
  <div className="border border-[#2D2A26] px-3 py-2 text-center" data-testid={testid}>
    <div className="font-display text-xl" style={{ color: color || "#E4DFD3" }}>{value}</div>
    <div className="font-mono text-[8px] uppercase tracking-[0.2em] text-[#7B7168] mt-0.5">{label}</div>
  </div>
);

const KIND_LABEL = {
  replacement: "REPLACEMENT FOUND",
  archived: "ARCHIVED CAPTURE",
  keep_original: "KEEP ORIGINAL",
  unresolved: "UNRESOLVED",
};

const SuggestionBlock = ({ item, token, userToken, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const sug = item.suggestion;
  if (!sug || sug.status !== "proposed") return null;
  const kind = sug.kind || "unresolved";
  const color = kind === "replacement" ? "#3FB950"
    : kind === "archived" ? "#58A6FF"
    : kind === "keep_original" ? "#E0A526" : "#D9A56C";

  const decide = async (decision) => {
    setBusy(true);
    try {
      await adminSourceLinkDecide(token, item.slug, item.url, decision, userToken);
      toast.success(decision === "approve" ? "Suggestion applied — citation history logged"
        : decision === "reject" ? "Suggestion rejected" : "Kept for manual review");
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Decision failed"); }
    finally { setBusy(false); }
  };

  const btn = "inline-flex items-center gap-1 px-2 py-1 border font-mono text-[8px] uppercase tracking-[0.18em] transition-colors disabled:opacity-40";
  return (
    <div className="mt-2 border p-2.5" style={{ borderColor: color, background: "#12110e" }}
         data-testid={`source-suggestion-${item.slug}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-[8px] uppercase tracking-[0.25em] px-1.5 py-0.5"
              style={{ background: color, color: "#0a0907" }}>{KIND_LABEL[kind]}</span>
        {sug.confidence && sug.confidence !== "n/a" && (
          <span className="font-mono text-[8px] uppercase tracking-[0.2em]" style={{ color }}>
            confidence: {sug.confidence}
          </span>
        )}
        {sug.source_type && <span className="font-mono text-[8px] text-[#7B7168]">{sug.source_type}</span>}
        {sug.publisher && <span className="font-mono text-[8px] text-[#7B7168]">· {sug.publisher}</span>}
        {sug.publication_date && <span className="font-mono text-[8px] text-[#7B7168]">· {sug.publication_date}</span>}
        {sug.archived_capture_date && <span className="font-mono text-[8px] text-[#58A6FF]">· capture {sug.archived_capture_date}</span>}
      </div>
      {sug.title && <div className="mt-1 text-[#E4DFD3] text-sm">{sug.title}</div>}
      {sug.url && <div className="font-mono text-[9px] text-[#A39E93] break-all">{sug.url}</div>}
      <div className="mt-1 font-mono text-[10px] text-[#C9C2B4] leading-relaxed">{sug.why}</div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {sug.url && (
          <a href={sug.url} target="_blank" rel="noreferrer"
             data-testid={`suggestion-preview-btn-${item.slug}`}
             className={`${btn} border-[#454038] text-[#A39E93] hover:border-[#58A6FF] hover:text-[#58A6FF]`}>
            <Eye size={9} /> Preview replacement
          </a>
        )}
        {sug.archived_url && (
          <a href={sug.archived_url} target="_blank" rel="noreferrer"
             data-testid={`suggestion-archive-open-btn-${item.slug}`}
             className={`${btn} border-[#454038] text-[#A39E93] hover:border-[#58A6FF] hover:text-[#58A6FF]`}>
            <Archive size={9} /> Open archive capture
          </a>
        )}
        {kind !== "unresolved" && (
          <button type="button" disabled={busy} onClick={() => decide("approve")}
                  data-testid={`suggestion-approve-btn-${item.slug}`}
                  className={`${btn} border-[#3FB950] text-[#3FB950] hover:bg-[#3FB950] hover:text-[#0a0907]`}>
            {busy ? <Loader2 size={9} className="animate-spin" /> : <CheckCircle2 size={9} />}
            {kind === "replacement" ? "Approve replacement" : "Attach archived copy"}
          </button>
        )}
        <button type="button" disabled={busy} onClick={() => decide("reject")}
                data-testid={`suggestion-reject-btn-${item.slug}`}
                className={`${btn} border-[#454038] text-[#A39E93] hover:border-[#D92D20] hover:text-[#D92D20]`}>
          <XCircle size={9} /> Reject
        </button>
        <button type="button" disabled={busy} onClick={() => decide("manual")}
                data-testid={`suggestion-manual-btn-${item.slug}`}
                className={`${btn} border-[#454038] text-[#A39E93] hover:border-[#E0A526] hover:text-[#E0A526]`}>
          <EyeOff size={9} /> Keep for manual review
        </button>
      </div>
    </div>
  );
};

const SourceRow = ({ item, token, userToken, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const [replacing, setReplacing] = useState(false);
  const [newUrl, setNewUrl] = useState("");
  const key = `${item.slug}::${item.url}`;
  const color = STATUS_COLOR[item.status] || "#A39E93";
  const suppressed = item.manual?.reviewed || item.manual?.temporarily_unavailable;

  const act = useCallback(async (fn, okMsg) => {
    setBusy(true);
    try { await fn(); toast.success(okMsg); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
    finally { setBusy(false); }
  }, [onChanged]);

  const btn = "inline-flex items-center gap-1 px-2 py-1 border font-mono text-[8px] uppercase tracking-[0.18em] transition-colors disabled:opacity-40";

  return (
    <div className={`border border-[#2D2A26] bg-[#0F0F0F] p-3 ${suppressed ? "opacity-60" : ""}`}
         data-testid={`source-health-item-${item.slug}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-[9px] uppercase tracking-[0.2em] px-2 py-0.5 border"
              style={{ color, borderColor: color }}>{item.status}</span>
        {item.http_status && <span className="font-mono text-[9px] text-[#7B7168]">HTTP {item.http_status}</span>}
        {suppressed && <span className="font-mono text-[8px] uppercase text-[#7B7168]">
          {item.manual?.reviewed ? "reviewed" : "marked temp-unavailable"}</span>}
        {item.archived_available && <span className="font-mono text-[8px] uppercase text-[#58A6FF]">archived copy exists</span>}
        <span className="ml-auto font-mono text-[8px] text-[#7B7168] inline-flex items-center gap-1">
          <Clock size={9} /> {_fmt(item.last_checked_at)}
        </span>
      </div>
      <div className="mt-1.5 text-[#E4DFD3] text-sm">{item.source_title || item.url}</div>
      <div className="font-mono text-[10px] text-[#A39E93]">
        {item.topic_title} <span className="text-[#7B7168]">({item.slug})</span>
      </div>
      <div className="font-mono text-[9px] text-[#7B7168] break-all mt-0.5">{item.url}</div>
      {item.reason && <div className="font-mono text-[10px] text-[#D9A56C] mt-1">// {item.reason}</div>}
      {item.previous_status && item.previous_status !== item.status && (
        <div className="font-mono text-[9px] text-[#7B7168] mt-0.5">was: {item.previous_status}</div>
      )}

      <SuggestionBlock item={item} token={token} userToken={userToken} onChanged={onChanged} />

      <div className="mt-2 flex flex-wrap gap-1.5">
        <button type="button" disabled={busy}
                data-testid={`source-recheck-btn-${item.slug}`}
                onClick={() => act(() => adminSourceLinkRecheck(token, item.slug, item.url, userToken), "Rechecked")}
                className={`${btn} border-[#454038] text-[#E4DFD3] hover:border-[#D92D20]`}>
          {busy ? <Loader2 size={9} className="animate-spin" /> : <RefreshCw size={9} />} Recheck now
        </button>
        <a href={item.url} target="_blank" rel="noreferrer"
           data-testid={`source-open-btn-${item.slug}`}
           className={`${btn} border-[#454038] text-[#A39E93] hover:border-[#58A6FF] hover:text-[#58A6FF]`}>
          <ExternalLink size={9} /> Open source
        </a>
        <button type="button" disabled={busy}
                data-testid={`source-replace-btn-${item.slug}`}
                onClick={() => setReplacing((v) => !v)}
                className={`${btn} border-[#454038] text-[#A39E93] hover:border-[#E0A526] hover:text-[#E0A526]`}>
          <Replace size={9} /> Replace source
        </button>
        <button type="button" disabled={busy}
                data-testid={`source-archive-btn-${item.slug}`}
                onClick={() => act(() => adminSourceLinkArchiveCopy(token, item.slug, item.url, userToken), "Archived copy attached")}
                className={`${btn} border-[#454038] text-[#A39E93] hover:border-[#58A6FF] hover:text-[#58A6FF]`}>
          <Archive size={9} /> Add archived copy
        </button>
        {!item.manual?.temporarily_unavailable && (
          <button type="button" disabled={busy}
                  data-testid={`source-mark-temp-btn-${item.slug}`}
                  onClick={() => act(() => adminSourceLinkMark(token, item.slug, item.url, "temporarily_unavailable", userToken), "Marked temporarily unavailable")}
                  className={`${btn} border-[#454038] text-[#A39E93] hover:border-[#E0A526] hover:text-[#E0A526]`}>
            <Clock size={9} /> Mark temp unavailable
          </button>
        )}
        {!item.manual?.reviewed && (
          <button type="button" disabled={busy}
                  data-testid={`source-mark-reviewed-btn-${item.slug}`}
                  onClick={() => act(() => adminSourceLinkMark(token, item.slug, item.url, "reviewed", userToken), "Marked reviewed")}
                  className={`${btn} border-[#454038] text-[#A39E93] hover:border-[#3FB950] hover:text-[#3FB950]`}>
            <EyeOff size={9} /> Mark reviewed
          </button>
        )}
      </div>

      {replacing && (
        <div className="mt-2 flex gap-2">
          <input value={newUrl} onChange={(e) => setNewUrl(e.target.value)}
                 placeholder="https:// replacement URL"
                 data-testid={`source-replace-input-${item.slug}`}
                 className="flex-1 bg-[#161512] border border-[#454038] px-2 py-1.5 font-mono text-[10px] text-[#E4DFD3] outline-none focus:border-[#D92D20]" />
          <button type="button" disabled={busy || !newUrl.startsWith("http")}
                  data-testid={`source-replace-confirm-btn-${item.slug}`}
                  onClick={() => act(async () => {
                    await adminSourceLinkReplace(token, item.slug, item.url, newUrl, userToken);
                    setReplacing(false);
                  }, "Source replaced — citation history logged")}
                  className={`${btn} border-[#3FB950] text-[#3FB950] hover:bg-[#3FB950] hover:text-[#0a0907]`}>
            Confirm
          </button>
        </div>
      )}
    </div>
  );
};

export const AdminSourceHealth = ({ token, userToken }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const load = useCallback(async () => {
    try { setData(await adminSourceLinkHealth(token, userToken)); }
    catch { toast.error("Could not load source health"); }
    finally { setLoading(false); }
  }, [token, userToken]);

  useEffect(() => { load(); }, [load]);

  const runNow = useCallback(async () => {
    setRunning(true);
    toast.info("Auditing every source link — this can take a few minutes…");
    try {
      const run = await adminSourceLinkAuditRun(token, userToken);
      toast.success(`Checked ${run.checked} sources · alerts: ${run.alerts?.length || 0}${run.email_sent ? " · digest emailed" : ""}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Audit failed");
    } finally { setRunning(false); }
  }, [token, userToken, load]);

  const failing = (data?.items || []).filter((i) => i.status !== "REDIRECTED");
  const redirected = (data?.items || []).filter((i) => i.status === "REDIRECTED");

  return (
    <div className="max-w-[1100px] mx-auto px-4 md:px-8 py-8" data-testid="source-health-panel">
      <div className="flex flex-wrap items-center gap-3 mb-2">
        <Link2 size={16} className="text-[#D92D20]" />
        <h1 className="font-display text-2xl text-[#E4DFD3] tracking-tight">Source Health</h1>
        {data?.last_run_at && (
          <span className="font-mono text-[10px] text-[#7B7168]" data-testid="source-health-last-run">
            last audit {_fmt(data.last_run_at)} · {data.last_trigger}
          </span>
        )}
        <button type="button" onClick={runNow} disabled={running}
                data-testid="source-audit-run-btn"
                className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#D92D20] text-[#D92D20] hover:bg-[#D92D20] hover:text-[#0a0907] font-mono text-[9px] uppercase tracking-[0.22em] disabled:opacity-40">
          {running ? <Loader2 size={11} className="animate-spin" /> : <Radar size={11} />}
          {running ? "Auditing…" : "Run audit now"}
        </button>
      </div>
      <p className="font-mono text-[10px] text-[#7B7168] uppercase tracking-[0.2em] mb-6">
        // Every claim stays source-backed — links audited nightly
      </p>

      <EvidenceRecoveryQueue token={token} userToken={userToken} />

      {loading && (
        <div className="flex items-center gap-2 text-[#A39E93] font-mono text-xs">
          <Loader2 size={14} className="animate-spin" /> Loading…
        </div>
      )}

      {!loading && data && (
        <>
          <div className="grid grid-cols-4 sm:grid-cols-8 gap-2 mb-6" data-testid="source-health-summary">
            <Chip label="Tracked" value={data.total_tracked} testid="source-chip-total" />
            <Chip label="Healthy" value={data.healthy} color="#3FB950" testid="source-chip-healthy" />
            <Chip label="Redirected" value={data.redirected} color="#58A6FF" testid="source-chip-redirected" />
            <Chip label="Restricted" value={data.access_restricted} color="#7B7168" testid="source-chip-restricted" />
            <Chip label="Temp fail" value={data.temporarily_unavailable} color="#E0A526" testid="source-chip-temp" />
            <Chip label="Broken" value={data.broken} color="#D92D20" testid="source-chip-broken" />
            <Chip label="Removed" value={data.removed} color="#D92D20" testid="source-chip-removed" />
            <Chip label="Review" value={data.needs_review} color="#D9A56C" testid="source-chip-review" />
          </div>
          <p className="font-mono text-[9px] text-[#7B7168] -mt-4 mb-6">
            Restricted = paywall / anti-bot walls (401·403·429) — readable by humans, tracked but never alerted.
          </p>

          {(data.evidence_review || []).length > 0 && (
            <div className="border border-[#D92D20] bg-[#1a0e0c] p-4 mb-6" data-testid="evidence-review-banner">
              <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20]">
                <AlertTriangle size={12} /> Evidence review required
              </div>
              {data.evidence_review.map((t) => (
                <div key={t.slug} className="mt-2 font-mono text-[10px] text-[#E4DFD3]">
                  • {t.title} <span className="text-[#7B7168]">({t.slug}) — every supporting source failed · flagged {_fmt(t.evidence_review_at)}</span>
                </div>
              ))}
            </div>
          )}

          {failing.length === 0 && (data.evidence_review || []).length === 0 && (
            <div className="border border-[#2D2A26] bg-[#0F0F0F] p-8 text-center" data-testid="source-health-all-clear">
              <CheckCircle2 size={22} className="mx-auto text-[#3FB950] mb-3" />
              <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#3FB950]">Sources healthy</div>
              <div className="mt-2 font-mono text-[10px] text-[#7B7168]">
                No broken, removed or repeatedly failing sources detected.
              </div>
            </div>
          )}

          {failing.length > 0 && (
            <div className="space-y-3" data-testid="source-health-failing-list">
              {failing.map((item) => (
                <SourceRow key={`${item.slug}::${item.url}`} item={item}
                           token={token} userToken={userToken} onChanged={load} />
              ))}
            </div>
          )}

          {redirected.length > 0 && (
            <details className="mt-6">
              <summary className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#58A6FF] cursor-pointer"
                       data-testid="source-health-redirected-toggle">
                Redirected sources ({redirected.length}) — healthy, new location recorded
              </summary>
              <div className="space-y-3 mt-3">
                {redirected.map((item) => (
                  <SourceRow key={`${item.slug}::${item.url}`} item={item}
                             token={token} userToken={userToken} onChanged={load} />
                ))}
              </div>
            </details>
          )}
        </>
      )}
    </div>
  );
};

export default AdminSourceHealth;
